import prettifyXml from 'prettify-xml';
import { format } from 'json-string-formatter';
import { XMLSerializer } from '@xmldom/xmldom';
import { map, isArray, isNumber, toString, has, isNil } from 'lodash';
import { ALL_FORMATS } from '../constants/attributeFormats';

export const bytesToString = (bytes, formatToUse = '.0') => {
  if (isNaN(bytes)) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let unitIndex = 0;

  while (bytes >= 1024 && unitIndex < units.length - 1) {
    bytes /= 1024;
    unitIndex++;
  }

  if (Number.isInteger(bytes)) {
    return `${bytes} ${units[unitIndex]}`;
  } else {
    return `${bytes.toFixed(1)} ${units[unitIndex]}`;
  }
};

export const formatCode = (code, contentType) => {
  switch (contentType) {
    case 'application/json':
      try {
        return format(code);
      } catch (error) {
        return code;
      }

    case 'application/xml':
      try {
        return prettifyXml(code, { indent: 2, newline: '\n' });
      } catch (error) {
        return code;
      }

    default:
  }

  return code;
};

export const stringifyObject = (object, contentType) => {
  switch (contentType) {
    case 'application/xml':
      const serializer = new XMLSerializer();

      if (isArray(object)) {
        return map(object, (node) => serializer.serializeToString(node)).join('\n');
      }

      return serializer.serializeToString(object);

    default: {
      try {
        let stringified = JSON.stringify(object);
        return stringified;
      } catch (e) {
        console.log(e);
        return null;
      }
    }
  }
};

export const StripHTMLFromString = (message = '') => {
  return message.replace(/<(?:.|\n)*?>/gm, '');
};

export const formatNamedNumber = (number, precision = 2) => {
  if (isNaN(number)) {
    return '0';
  }
  if (number >= 1000000) {
    const units = ['', 'K', ' Million', ' Billion', ' Trillion'];
    let unitIndex = 0;

    while (number >= 1000 && unitIndex < units.length - 1) {
      number /= 1000;
      unitIndex++;
    }

    // if precision is -1, skip rounding
    if (precision < 0) {
      return `${number}${units[unitIndex]}`;
    }

    return `${number.toFixed(precision)}${units[unitIndex]}`;
  } else {
    return formatNumber(number);
  }
};

export const formatNumber = (value) => {
  let formattedValue = isNumber(value) ? new Intl.NumberFormat('en-US').format(value) : value;
  return toString(formattedValue);
};

export const getAttributeFormatDisplay = (attr = null) => {
  if (isNil(attr)) {
    return null;
  }
  const type = has(attr, '__type') ? attr.__type : 'null';

  let formatPretty = null;
  if (has(attr, '__format') && !isNil(attr.__format)) {
    formatPretty = has(ALL_FORMATS, attr.__format)
      ? ALL_FORMATS[attr.__format].label
      : attr.__format;
  }

  return formatPretty ? `${formatPretty} ${type}` : type;
};

export const getColorFromId = (id = 0) => {
  const h = isNumber(id) ? (id * 61.8033988749895) % 360 : 0, //multiply by Golden ratio (sort of) to spread out ids
    s = 0.5,
    v = 0.75;
  const hp = h / 60,
    c = s * v,
    m = v - c;
  let r, g, b;

  const x = c * (1 - Math.abs((hp % 2) - 1));

  switch (Math.floor(hp)) {
    case 0:
      {
        ((r = c), (g = x), (b = 0));
      }
      break;
    case 1:
      {
        ((r = x), (g = c), (b = 0));
      }
      break;
    case 2:
      {
        ((r = 0), (g = c), (b = x));
      }
      break;
    case 3:
      {
        ((r = 0), (g = x), (b = c));
      }
      break;
    case 4:
      {
        ((r = x), (g = 0), (b = c));
      }
      break;
    case 5:
      {
        ((r = c), (g = 0), (b = x));
      }
      break;
    default:
      ((r = 0), (g = 0), (b = 0));
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `rgb(${r},${g}, ${b})`;
};

export default {
  bytesToString,
  formatNumber,
  formatNamedNumber,
  formatCode,
  stringifyObject,
  StripHTMLFromString,
  getAttributeFormatDisplay,
  getColorFromId,
};
